import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { SiteFooter } from "@/components/SiteFooter";
import { ArrowLeft, ArrowRight, Loader2, MapPin, Wallet, BedDouble, Bath, Ruler, BadgeCheck, Search, Handshake, Share2, Check, CalendarDays, Users } from "lucide-react";
import { PublicHeader } from "@/components/PublicHeader";
import { Navbar } from "@/components/Navbar";
import { api, apiError, peso } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { ShareDialog } from "@/components/ShareDialog";
import { buyerNeedShareUrl, publicSiteUrl } from "@/lib/share";
import { propertyTypeLabel } from "@/lib/taxonomy";
import { proProfilePath, trackProfileClick } from "@/lib/proProfile";
import { Seo } from "@/components/Seo";

const budgetLabel = (n) => {
  if (n.listing_type === "Unit Hosting") {
    const lo = n.nightly_budget_min || 0, hi = n.nightly_budget_max || 0;
    if (lo && hi) return `${peso(lo)} – ${peso(hi)} / night`;
    if (hi) return `Up to ${peso(hi)} / night`;
    if (lo) return `${peso(lo)}+ / night`;
    return "Nightly budget flexible";
  }
  const lo = n.budget_min || 0, hi = n.budget_max || 0;
  if (lo && hi) return `${peso(lo)} – ${peso(hi)}`;
  if (hi) return `Up to ${peso(hi)}`;
  if (lo) return `${peso(lo)}+`;
  return "Budget flexible";
};

function Spec({ icon: Icon, label, value }) {
  if (!value) return null;
  return (
    <div className="flex items-center gap-2 text-sm text-slate-700"><Icon className="h-4 w-4 text-[#0F2C59]" /> <span>{label}: <span className="font-semibold">{value}</span></span></div>
  );
}

export default function PublicBuyerNeedDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [n, setN] = useState(null);
  const [err, setErr] = useState(false);
  const [open, setOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);

  const isPro = user && (user.account_type || "PROFESSIONAL") !== "CONSUMER";

  useEffect(() => {
    let active = true;
    api.get(`/public/buyer-needs/${id}`)
      .then((r) => { if (active) setN(r.data); })
      .catch(() => { if (active) setErr(true); });
    return () => { active = false; };
  }, [id]);

  // SEO: public-safe title/description/OG + canonical. Never any buyer PII.
  useEffect(() => {
    if (!n) return;
    const loc = (n.cities || []).slice(0, 2).join(", ") || "the Philippines";
    const isStay = n.listing_type === "Unit Hosting";
    const kind = isStay ? "Stay" : (n.listing_type === "For Rent" ? "For Rent" : "For Sale");
    const title = `Buyer looking for ${n.property_type || "property"} in ${loc} — PropertyConnect`;
    const desc = `A buyer represented by a PropertyConnect professional is looking (${kind}) for a ${n.property_type || "property"} in ${loc}. Budget ${budgetLabel(n)}.`;
    const url = `${publicSiteUrl()}/buyer-need/${n.id}`;
    const img = `${publicSiteUrl()}/brand/pc-buyer-need-og.png`;
    const prevTitle = document.title;
    document.title = title;
    const set = (key, val, prop = true) => {
      const a = prop ? "property" : "name";
      let el = document.head.querySelector(`meta[${a}="${key}"]`);
      if (!el) { el = document.createElement("meta"); el.setAttribute(a, key); document.head.appendChild(el); }
      el.setAttribute("content", val);
    };
    set("description", desc, false);
    set("og:site_name", "PropertyConnect");
    set("og:title", title);
    set("og:description", desc);
    set("og:image", img);
    set("og:type", "website");
    set("og:url", url);
    set("twitter:card", "summary_large_image", false);
    set("twitter:title", title, false);
    set("twitter:description", desc, false);
    set("twitter:image", img, false);
    let canon = document.head.querySelector('link[rel="canonical"]');
    const hadCanon = !!canon; const prevCanon = canon && canon.getAttribute("href");
    if (!canon) { canon = document.createElement("link"); canon.setAttribute("rel", "canonical"); document.head.appendChild(canon); }
    canon.setAttribute("href", url);
    return () => {
      document.title = prevTitle;
      if (hadCanon) canon.setAttribute("href", prevCanon || ""); else canon.remove();
    };
  }, [n]);

  const copyLink = async () => {
    try { await navigator.clipboard.writeText(buyerNeedShareUrl(id)); toast.success("Link copied"); }
    catch { toast.error("Could not copy link"); }
  };

  if (err) return (
    <div className="min-h-screen bg-slate-50">{user ? <Navbar /> : <PublicHeader />}
      <div className="max-w-2xl mx-auto px-4 py-24 text-center">
        <Search className="h-10 w-10 text-slate-300 mx-auto" />
        <h1 className="font-display text-2xl font-bold text-slate-900 mt-4">Buyer Need not available</h1>
        <p className="text-slate-500 mt-2 text-sm">This Buyer Need is no longer public or has been closed.</p>
        <Link to="/marketplace?mode=buyer-needs" className="inline-block mt-6"><Button className="bg-[#0F2C59] text-white rounded-xl">Browse Buyer Needs</Button></Link>
      </div>
    </div>
  );

  if (!n) return <div className="min-h-screen bg-slate-50">{user ? <Navbar /> : <PublicHeader />}<div className="flex justify-center py-32"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div></div>;

  const owner = n.owner || {};
  const location = (n.cities || []).join(", ") || "Anywhere";
  const reqFeats = n.required_features || [];
  const prefFeats = [...(n.preferred_features || []), ...(n.must_haves || [])].filter((v, i, a) => v && a.indexOf(v) === i && !reqFeats.includes(v));

  return (
    <div className="min-h-screen bg-slate-50" data-testid="public-buyer-need-page">
      <Seo robots="noindex,follow" />
      {user ? <Navbar /> : <PublicHeader />}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
        <Link to="/marketplace?mode=buyer-needs" data-testid="back-to-buyer-needs" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-[#0F2C59] mb-4"><ArrowLeft className="h-4 w-4" /> Back to Buyer Needs</Link>

        <div className="bg-[#0F2C59] text-white rounded-2xl p-6 sm:p-8">
          <span className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wide px-2.5 py-1 rounded-full bg-white/15"><Search className="h-3 w-3" /> Buyer Looking For · {n.listing_type === "Unit Hosting" ? "Stay" : n.listing_type}</span>
          <h1 className="font-display text-3xl sm:text-4xl font-bold mt-3" data-testid="buyer-need-title">{n.buyer_alias}</h1>
          <div className="flex flex-wrap gap-x-6 gap-y-2 mt-3 text-slate-300 text-sm">
            <span className="flex items-center gap-1.5"><MapPin className="h-4 w-4" /> {location}</span>
            <span className="flex items-center gap-1.5"><Wallet className="h-4 w-4" /> {budgetLabel(n)}</span>
            <span>{propertyTypeLabel(n.property_type) || "Any type"}</span>
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-6 mt-6">
          <div className="md:col-span-2 space-y-6">
            <div className="bg-white border border-slate-200 rounded-2xl p-6">
              <h2 className="font-display text-lg font-bold text-slate-900 mb-4">Property Requirements</h2>
              <div className="grid sm:grid-cols-2 gap-3">
                <Spec icon={MapPin} label="Location" value={location} />
                <Spec icon={Wallet} label={n.listing_type === "Unit Hosting" ? "Nightly budget" : "Budget"} value={budgetLabel(n)} />
                {n.listing_type === "Unit Hosting" ? (
                  <>
                    <Spec icon={Users} label="Guests" value={n.stay_guests > 0 ? `${n.stay_guests}` : null} />
                    <Spec icon={BedDouble} label="Min beds" value={n.stay_beds_min > 0 ? `${n.stay_beds_min}+` : null} />
                    <Spec icon={CalendarDays} label="Check-in" value={n.stay_check_in || null} />
                    <Spec icon={CalendarDays} label="Check-out" value={n.stay_check_out || null} />
                  </>
                ) : (
                  <>
                    <Spec icon={BedDouble} label="Min bedrooms" value={n.min_bedrooms > 0 ? `${n.min_bedrooms}+` : null} />
                    <Spec icon={Bath} label="Min bathrooms" value={n.min_bathrooms > 0 ? `${n.min_bathrooms}+` : null} />
                    <Spec icon={Ruler} label="Floor area" value={n.floor_area_min || n.floor_area_max ? `${n.floor_area_min || "?"}–${n.floor_area_max || "?"} sqm` : null} />
                    <Spec icon={Ruler} label="Lot area" value={n.lot_area_min || n.lot_area_max ? `${n.lot_area_min || "?"}–${n.lot_area_max || "?"} sqm` : null} />
                  </>
                )}
                <Spec icon={Check} label="Furnishing" value={n.furnishing_pref && n.furnishing_pref !== "Any" ? n.furnishing_pref : null} />
                <Spec icon={Check} label="Completion" value={n.completion_pref || null} />
              </div>
              {n.public_description && (
                <div className="mt-4 pt-4 border-t border-slate-100">
                  <p className="text-sm text-slate-700 whitespace-pre-line" data-testid="buyer-need-description">{n.public_description}</p>
                </div>
              )}
            </div>

            {(reqFeats.length > 0 || prefFeats.length > 0) && (
              <div className="bg-white border border-slate-200 rounded-2xl p-6">
                <h2 className="font-display text-lg font-bold text-slate-900 mb-4">Amenities & preferences</h2>
                {reqFeats.length > 0 && <>
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-2">Required</p>
                  <div className="flex flex-wrap gap-2 mb-4">{reqFeats.map((r) => <span key={r} className="text-sm font-medium bg-[#0F2C59] text-white px-3 py-1 rounded-full">{r}</span>)}</div>
                </>}
                {prefFeats.length > 0 && <>
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-2">Preferred</p>
                  <div className="flex flex-wrap gap-2">{prefFeats.map((r) => <span key={r} className="text-sm font-medium bg-slate-100 text-slate-600 px-3 py-1 rounded-full">{r}</span>)}</div>
                </>}
              </div>
            )}
          </div>

          <div className="space-y-4">
            <div className="bg-white border border-slate-200 rounded-2xl p-6 sticky top-6">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-3">Represented by</p>
              {(() => {
                const path = proProfilePath(owner);
                const head = (
                  <div className="flex items-center gap-3">
                    {owner.avatar ? <img src={owner.avatar} alt="" className="h-11 w-11 rounded-full bg-slate-100" /> : <div className="h-11 w-11 rounded-full bg-slate-200" />}
                    <div className="min-w-0">
                      <p className="font-semibold text-slate-900 truncate flex items-center gap-1">{owner.name}{owner.verification_status === "PROFESSIONAL_VERIFIED" && <BadgeCheck className="h-4 w-4 text-[#0F2C59]" title="Verified professional" />}</p>
                      <p className="text-xs text-slate-500 truncate">{owner.role_label}{owner.agency ? ` · ${owner.agency}` : ""}</p>
                    </div>
                  </div>
                );
                return path ? (
                  <Link to={path} data-testid="buyer-need-pro-profile-link" onClick={() => trackProfileClick(owner, "buyer_need_detail")}
                    className="group block rounded-lg -m-1 p-1 hover:bg-slate-50 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#C79A3B]/50">
                    {head}
                    <span className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-[#C79A3B] group-hover:gap-1.5 transition-all">View Professional Profile <ArrowRight className="h-3.5 w-3.5" /></span>
                  </Link>
                ) : head;
              })()}
              <Button data-testid="open-match-response" onClick={() => setOpen(true)} className="w-full mt-5 bg-[#C79A3B] hover:bg-[#C79A3B]/90 text-white rounded-xl font-semibold h-11"><Handshake className="h-4 w-4 mr-2" /> I Have a Matching Property</Button>
              <Button data-testid="share-buyer-need" onClick={() => setShareOpen(true)} variant="outline" className="w-full mt-2 rounded-xl border-slate-300"><Share2 className="h-4 w-4 mr-2" /> Share</Button>
              <ShareDialog
                open={shareOpen}
                onOpenChange={setShareOpen}
                url={buyerNeedShareUrl(n.id)}
                title={`Buyer looking for ${n.property_type || "property"} in ${(n.cities || []).slice(0, 2).join(", ") || "the Philippines"}`}
                text={`${n.listing_type === "Unit Hosting" ? "Stay requirement" : "Buyer looking"} — ${n.property_type || "property"} in ${(n.cities || []).slice(0, 2).join(", ") || "the Philippines"} · ${budgetLabel(n)}`}
                heading="Share this Buyer Need"
                subtitle="Share this public Buyer Need with your professional network. No buyer contact details are ever exposed."
              />
              <p className="text-[11px] text-slate-400 mt-3 text-center">The buyer's identity and contact details stay private.</p>
            </div>
          </div>
        </div>
      </div>

      <RespondDialog open={open} onOpenChange={setOpen} need={n} isPro={isPro} user={user} nav={nav} />
      <SiteFooter variant={user ? "compact" : "full"} />
    </div>
  );
}

function RespondDialog({ open, onOpenChange, need, isPro, user, nav }) {
  const [props, setProps] = useState(null);
  const [propId, setPropId] = useState("");
  const [form, setForm] = useState({ full_name: "", email: "", mobile: "", message: "" });
  const [busy, setBusy] = useState(false);
  const authed = !!user;

  useEffect(() => {
    if (!open || !isPro) return;
    api.get("/me/properties?status=Active").then((r) => setProps(r.data || [])).catch(() => setProps([]));
  }, [open, isPro]);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const submitCollab = async () => {
    if (!propId) { toast.error("Select one of your properties."); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/collaborations", { property_id: propId, buyer_requirement_id: need.id, note: form.message });
      toast.success("Collaboration started with the representing professional");
      onOpenChange(false);
      nav(`/collaboration/${data.id}`);
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const submitResponse = async () => {
    if (!authed && (!form.full_name || !form.email)) { toast.error("Name and email are required."); return; }
    if (!form.message.trim()) { toast.error("Add a short message about your property."); return; }
    setBusy(true);
    try {
      await api.post(`/public/buyer-needs/${need.id}/responses`, form);
      toast.success("Your response was sent to the representing professional");
      onOpenChange(false);
      setForm({ full_name: "", email: "", mobile: "", message: "" });
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="respond-dialog">
        <DialogHeader>
          <DialogTitle>I Have a Matching Property</DialogTitle>
          <DialogDescription>Reach the professional representing this buyer. The buyer's identity stays private.</DialogDescription>
        </DialogHeader>

        {isPro ? (
          <div className="space-y-3">
            <div>
              <Label>Select one of your active listings</Label>
              <Select value={propId} onValueChange={setPropId}>
                <SelectTrigger data-testid="collab-property-select" className="mt-1.5"><SelectValue placeholder={props === null ? "Loading…" : (props.length ? "Choose a property" : "No active listings")} /></SelectTrigger>
                <SelectContent>{(props || []).map((p) => <SelectItem key={p.id} value={p.id}>{p.title} · {p.city}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Note <span className="text-slate-400 font-normal">(optional)</span></Label><Textarea data-testid="collab-note" value={form.message} onChange={(e) => set("message", e.target.value)} placeholder="Why this property fits the buyer's need…" className="mt-1.5" /></div>
            <DialogFooter>
              <Button data-testid="submit-collab" onClick={submitCollab} disabled={busy || !props?.length} className="bg-[#0F2C59] text-white rounded-xl">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Start collaboration"}</Button>
            </DialogFooter>
            {props && props.length === 0 && <p className="text-xs text-slate-500">You have no active listings yet. <Link to="/have-property" className="text-[#0F2C59] font-semibold underline">Add a property</Link> to collaborate.</p>}
          </div>
        ) : (
          <div className="space-y-3">
            {!authed && <>
              <div><Label>Full name</Label><Input data-testid="resp-name" value={form.full_name} onChange={(e) => set("full_name", e.target.value)} className="mt-1.5" /></div>
              <div><Label>Email</Label><Input data-testid="resp-email" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} className="mt-1.5" /></div>
              <div><Label>Mobile <span className="text-slate-400 font-normal">(optional)</span></Label><Input data-testid="resp-mobile" value={form.mobile} onChange={(e) => set("mobile", e.target.value)} className="mt-1.5" /></div>
            </>}
            <div><Label>Your message</Label><Textarea data-testid="resp-message" value={form.message} onChange={(e) => set("message", e.target.value)} placeholder="Describe the matching property (location, price, key features)…" className="mt-1.5" /></div>
            <DialogFooter>
              <Button data-testid="submit-response" onClick={submitResponse} disabled={busy} className="bg-[#C79A3B] text-white rounded-xl">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Send response"}</Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
